// ReportingChartGrid — the ONE draggable + resizable shell BOTH reporting
// dashboards render their KPI TILES and their CHARTS through (Meta `/reporting`
// and Google `/reporting/google-ads`).
//
// It is a LAYOUT capability wrapped around what already exists. It invents no
// charts, changes no chart or tile logic, and touches none of the tile
// selection, the per-tile style toggles, the KPI values, the multi-metric series
// or the competitiveness block —
// they are handed in as `node` and rendered untouched. Living in the reporting
// module and being imported by googleAds is the same posture that module already
// takes with ReportingMiniChart / ChartStyleToggle / trendSeries: one chart
// stack, so the two dashboards cannot drift.
//
// ⚠️ TWO INDEPENDENT REGIONS PER PAGE, ONE FIXED ANCHOR. Each dashboard renders
// this shell TWICE — a KPI TILES grid above a CHARTS grid — each with its own
// stored layout, so rearranging tiles never disturbs the charts. The
// explorer/breakdown table below stays a FIXED block: it is the page's
// structural anchor, and a page whose anchors can be dragged away can render as
// "broken" with no way back.
//
// ⚠️ DRAG BY THE GRIP, NOT THE PANEL. Charts have interactive innards (clickable
// legends, tooltips, style toggles) and a KPI TILE is itself click-to-select
// (clicking it chooses the metric the big chart plots), so dragging the whole
// panel would fight that on every click. `draggableHandle` points at a dedicated
// grip. Charts get the shell's own grip strip above the card; a TILE is too
// small to wear a strip, so it hosts `ChartGripHandle` in its own header beside
// the style toggle and sets `providesOwnGrip` — see that component for why the
// grip must stop CLICK but never MOUSEDOWN. Resize is react-grid-layout's own
// bottom-right corner handle.
//
// ⚠️ THE PANEL DRAWS NO CARD OF ITS OWN. Every chart AND every KPI tile already
// renders its own bordered card with its own title and style toggle. A shell
// card around a self-carded panel is a card in a card, and rehoming those
// components' headers into a shared one is a large visual change with no way to
// eyeball it. So the grid item is transparent: the existing card IS the panel.
// The panel-fill CSS that lets a resized panel actually change the CHART's or
// MINI-CHART's height (rather than leave a taller card with the same canvas
// inside it) lives in src/index.css under `.reporting-chart-panel-body` — see
// the comment there, and note that a tile's deeper nesting needs the explicit
// `.reporting-chart-panel-grow` opt-in.
//
// ⚠️ SINGLE PERSISTED BREAKPOINT (lg). Below `lg` the grid drops to one column
// and is READ-ONLY: react-grid-layout auto-stacks the panels full width and both
// drag and resize are disabled, so a narrow viewport can never write a layout
// that then governs the desktop view. These are desktop tools.

import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { GripHorizontal, GripVertical, RotateCcw } from 'lucide-react';
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { cn } from '../../lib/utils';
import {
  CHART_GRID_COLS,
  CHART_GRID_MARGIN,
  CHART_GRID_ROW_HEIGHT,
  CHART_PANEL_MIN_H,
  CHART_PANEL_MIN_W,
  reconcileLayout,
  type ChartPanelLayout,
  type ChartPanelSpec,
} from '../../lib/reporting/chartLayout';

/** One arrangeable panel. `node` is the existing chart/tile, unchanged. */
export interface ChartPanel extends ChartPanelSpec {
  /** Only used for the grip's tooltip — the chart/tile draws its own title. */
  title: string;
  node: ReactNode;
  /**
   * Optional control rendered above the chart inside the panel (Google's
   * multi-metric "Series" picker). Kept out of `node` so the panel body has
   * exactly ONE child, which is what makes the fill CSS unambiguous.
   */
  toolbar?: ReactNode;
  /**
   * Set when `node` renders its OWN `ChartGripHandle` (the KPI tiles do, in
   * their header beside the style toggle). The shell then draws no grip strip of
   * its own — two handles on one panel would be a bug, and a 16px strip above
   * each tile in a 4-up row reads as visual noise.
   */
  providesOwnGrip?: boolean;
}

/**
 * The drag handle a panel can host inside its own header (KPI tiles).
 *
 * ⚠️ IT STOPS `click` BUT NEVER `mousedown`. react-draggable listens for
 * mousedown on the grid ITEM — an ancestor — so stopping mousedown here would
 * block dragging entirely. Stopping click is what keeps the gesture from also
 * firing the tile's own click-to-select and changing the plotted metric, the
 * same device the tile's style toggle already uses.
 */
export function ChartGripHandle({ title }: { title?: string }): React.JSX.Element {
  return (
    <span
      className="reporting-chart-grip -my-0.5 inline-flex shrink-0 cursor-grab items-center rounded px-0.5 text-muted-foreground/40 transition-colors hover:bg-accent hover:text-muted-foreground active:cursor-grabbing"
      title={title ?? 'Drag to move · resize from the bottom-right corner'}
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical className="h-3.5 w-3.5" aria-hidden />
    </span>
  );
}

// Created ONCE at module scope. Building it inside the component would mint a new
// component type every render and remount every chart on every keystroke.
const GridLayout = WidthProvider(Responsive);

const LG = 'lg';
const BREAKPOINTS = { lg: 1024, xs: 0 } as const;
const COLS = { lg: CHART_GRID_COLS, xs: 1 } as const;

function toRglLayout(layout: readonly ChartPanelLayout[], panels: readonly ChartPanel[]): Layout[] {
  const specs = new Map(panels.map((p) => [p.key, p]));
  return layout.map((item) => {
    const spec = specs.get(item.key);
    return {
      i: item.key,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      minW: spec?.minW ?? CHART_PANEL_MIN_W,
      minH: spec?.minH ?? CHART_PANEL_MIN_H,
    };
  });
}

function fromRglLayout(layout: readonly Layout[]): ChartPanelLayout[] {
  return layout.map((item) => ({ key: item.i, x: item.x, y: item.y, w: item.w, h: item.h }));
}

export function ReportingChartGrid({
  panels,
  layout,
  onLayoutChange,
  onReset,
  className,
}: {
  /** The panels CURRENTLY shown — the chart menu still governs which exist. */
  panels: ChartPanel[];
  /** The stored layout (decoded from gridPreferences). */
  layout: readonly ChartPanelLayout[];
  /** Fired on drag/resize STOP only, never on every tick. */
  onLayoutChange: (next: ChartPanelLayout[]) => void;
  /**
   * Renders a "Reset layout" control that clears the stored arrangement.
   *
   * ⚠️ THE TWO REPORTING DASHBOARDS NO LONGER PASS THIS. Since the save model
   * landed, their Reset means "discard my personal view and go back to the team
   * default" — a bigger, confirmable action that belongs beside the label saying
   * which view you are on, so it lives in the page toolbar
   * (ReportingViewActions) instead. Offering both would be two Resets with
   * different scopes, one of which quietly does less than the user expects.
   *
   * Kept on the shell because it is the right control for any FUTURE grid that
   * has an arrangement but no team-default layer.
   */
  onReset?: () => void;
  className?: string;
}): React.JSX.Element | null {
  // The grid is read-only below lg, so a phone can never author the desktop
  // layout. Starts at lg because WidthProvider measures on mount.
  const [breakpoint, setBreakpoint] = useState<string>(LG);
  const interactive = breakpoint === LG;

  // ⚠️ THE RECHARTS REMEASURE. recharts' ResponsiveContainer observes its own
  // element, so the fill CSS alone usually reflows a resized chart — but that is
  // the known fragile spot (a container that measures the parent that measures
  // it can settle at the old size). Bumping a per-panel nonce on resize-stop
  // REMOUNTS just that panel's chart subtree at its final size, which is
  // deterministic rather than hopeful. Keyed per panel so resizing one chart
  // never remounts the others.
  const [resizeNonce, setResizeNonce] = useState<Record<string, number>>({});
  const bumpNonce = useCallback((key: string) => {
    setResizeNonce((n) => ({ ...n, [key]: (n[key] ?? 0) + 1 }));
  }, []);

  // NOTE: RGL's own `onLayoutChange` is deliberately NOT wired. It fires on
  // mount and on every breakpoint change, neither of which is a user
  // rearranging anything — saving from it would PATCH preferences on every page
  // load. Only drag-stop and resize-stop persist.
  const resolved = useMemo(() => reconcileLayout(layout, panels), [layout, panels]);
  const rglLayout = useMemo(() => toRglLayout(resolved, panels), [resolved, panels]);

  const commit = useCallback(
    (next: readonly Layout[]) => {
      if (!interactive) return; // never persist a stacked narrow-screen layout
      onLayoutChange(fromRglLayout(next));
    },
    [interactive, onLayoutChange],
  );

  if (panels.length === 0) return null;

  return (
    <div
      className={cn('reporting-chart-grid', className)}
      // Drives the CSS that hides every grip below `lg`, where the grid is
      // read-only — a visible handle that cannot drag is a lie.
      data-interactive={interactive ? 'true' : 'false'}
    >
      {onReset && (
        <div className="mb-1 flex justify-end">
          <button
            type="button"
            onClick={onReset}
            title="Put the KPI tiles and the charts back to their default arrangement"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" aria-hidden />
            Reset layout
          </button>
        </div>
      )}

      <GridLayout
        className="layout"
        // Only `lg` is ever stored. RGL derives the single-column stack for
        // narrower widths itself, so there is no second layout to keep in sync.
        layouts={{ [LG]: rglLayout }}
        breakpoints={{ ...BREAKPOINTS }}
        cols={{ ...COLS }}
        rowHeight={CHART_GRID_ROW_HEIGHT}
        margin={CHART_GRID_MARGIN}
        containerPadding={[0, 0]}
        // ⚠️ DELIBERATELY NOT `measureBeforeMount`. It renders an empty div
        // until the container has been measured, so if the width observation
        // ever fails to fire the whole chart region is blank forever. Without
        // it, WidthProvider's initial state is a 1280px desktop width — already
        // above the lg breakpoint — so the first paint is the correct grid at a
        // near-enough width and the observation just corrects it. Trading a
        // one-frame width nudge for "cannot render blank" is the right way
        // round.
        isDraggable={interactive}
        isResizable={interactive}
        // The whole point: dragging starts on the grip, not on the chart.
        draggableHandle=".reporting-chart-grip"
        resizeHandles={['se']}
        // Charts are read top-to-bottom; vertical compaction keeps them from
        // drifting into a scattered field of floating cards.
        compactType="vertical"
        onBreakpointChange={(bp) => setBreakpoint(bp)}
        onDragStop={(next) => commit(next)}
        onResizeStop={(next, _old, item) => {
          commit(next);
          bumpNonce(item.i);
          // Belt and braces alongside the remount: some chart containers listen
          // for a window resize rather than observing their own element.
          if (typeof window !== 'undefined') window.dispatchEvent(new Event('resize'));
        }}
      >
        {panels.map((panel) => (
          <div key={panel.key} className="reporting-chart-panel">
            {/* A panel that hosts its own grip (the KPI tiles) gets no strip —
                two handles on one panel would be a bug. */}
            {!panel.providesOwnGrip && (
              <div
                className="reporting-chart-grip group/grip flex h-4 shrink-0 cursor-grab items-center justify-center active:cursor-grabbing"
                title={`Drag to move “${panel.title}” · resize from its bottom-right corner`}
              >
                <GripHorizontal
                  className="h-3 w-3 text-muted-foreground/40 transition-colors group-hover/grip:text-muted-foreground"
                  aria-hidden
                />
              </div>
            )}
            {panel.toolbar && (
              <div className="reporting-chart-panel-toolbar mb-2 flex shrink-0 justify-end">
                {panel.toolbar}
              </div>
            )}
            <div
              // The remount key — see the resizeNonce note above.
              key={`${panel.key}:${resizeNonce[panel.key] ?? 0}`}
              className="reporting-chart-panel-body"
            >
              {panel.node}
            </div>
          </div>
        ))}
      </GridLayout>
    </div>
  );
}
